import type { GeometryResponse, TrackPoint } from '@xingroute/schema'

const R = 6_371_000

function toRad(d: number): number {
  return (d * Math.PI) / 180
}

function haversine(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const dLat = toRad(b.lat - a.lat)
  const dLon = toRad(b.lon - a.lon)
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s))
}

function cumulativeDistancesMeters(track: TrackPoint[]): number[] {
  const distances = new Array(track.length).fill(0) as number[]
  for (let i = 1; i < track.length; i++) {
    distances[i] = distances[i - 1]! + haversine(track[i - 1]!, track[i]!)
  }
  return distances
}

/**
 * Locate the cumulative distance (in km) at which a waypoint sits along
 * the original track, by snapping it to the nearest track point. This is
 * cheaper than full polyline projection and accurate to the GPX sample
 * spacing, which is enough for re-sorting waypoints after a reverse.
 *
 * Returns `undefined` if the track is empty so callers can preserve any
 * pre-existing `distance_along_track` they were given.
 */
function nearestKmAlongTrack(
  track: TrackPoint[],
  waypoint: Pick<GeometryResponse['points'][number], 'lat' | 'lon'>,
  cumulativeMeters: readonly number[],
): number | undefined {
  if (track.length === 0) return undefined
  let bestIdx = 0
  let bestDistSq = Infinity
  for (let i = 0; i < track.length; i++) {
    const dLat = track[i]!.lat - waypoint.lat
    const dLon = track[i]!.lon - waypoint.lon
    const d2 = dLat * dLat + dLon * dLon
    if (d2 < bestDistSq) {
      bestDistSq = d2
      bestIdx = i
    }
  }
  return (cumulativeMeters[bestIdx] ?? 0) / 1000
}

/**
 * Pure reverse of a route's geometry for local preview.
 *
 * - `line` order flips end-to-end (lat/lon/ele preserved).
 * - Each waypoint's `distance_along_track` is recomputed as
 *   `totalKm - oldDistance`, then waypoints are re-sorted ascending so the
 *   waypoint list and GPX export read top-down in the reversed direction.
 * - Waypoint `lat`/`lon`/`name` etc. are NOT changed — only the order and
 *   the cached `distance_along_track`.
 * - `etag` is preserved so optimistic-concurrency callers can still detect
 *   server-side changes (the reverse is purely client-side and does not
 *   touch the canonical blob).
 *
 * Double-reverse is idempotent up to floating-point distance recomputation.
 */
export function reverseGeometry(geometry: GeometryResponse): GeometryResponse {
  const reversedTrack: TrackPoint[] = [...geometry.line].reverse()
  const cumulativeMeters = cumulativeDistancesMeters(geometry.line)
  const totalKm = (cumulativeMeters[cumulativeMeters.length - 1] ?? 0) / 1000

  const reversedWaypoints: GeometryResponse['points'] = geometry.points.map((wp) => {
    const previousKm =
      typeof wp.distance_along_track === 'number'
        ? wp.distance_along_track
        : nearestKmAlongTrack(geometry.line, wp, cumulativeMeters)
    const next: GeometryResponse['points'][number] =
      previousKm !== undefined && totalKm > 0
        ? { ...wp, distance_along_track: Math.max(0, totalKm - previousKm) }
        : { ...wp }
    return next
  })

  reversedWaypoints.sort((a, b) => {
    const aKm = a.distance_along_track ?? Number.POSITIVE_INFINITY
    const bKm = b.distance_along_track ?? Number.POSITIVE_INFINITY
    return aKm - bKm
  })

  return {
    ...geometry,
    line: reversedTrack,
    points: reversedWaypoints,
  }
}
